// Deterministic, content-agnostic rotation engine (Phase 2 scheduling engine).
//
// Picks the next item from a content-set pool given a rotation mode and a stable
// key (built from sync-group/device + day + content-set + a play counter). Pure
// and seeded — every device in a sync group, and the server's look-ahead queue,
// pick identically, so multi-room sync stays byte-identical and the result is
// golden-testable. Shared in player-core so the server queue-builder and the
// player run the exact same code.

import { seededShuffle, seededUnitFloat } from "./seeded-shuffle.ts";

export type RotationMode = "even" | "weighted" | "sequential";

export interface RotationItem {
  id: string;
  /** relative weight for "weighted" mode; defaults to 1; <=0 is treated as 0 */
  weight?: number | null;
}

/** Deterministic unit float in [0,1) derived from a string seed. */
function unitFloat(seed: string): number {
  return seededUnitFloat(seed);
}

/**
 * Effective weight in "weighted" mode: positive weights as given, exactly 0
 * disables the item, absent/null defaults to 1. Must stay byte-identical to
 * what pickRotationItem draws with — the materialization starvation guard
 * uses it to decide which items are guaranteed a slot.
 */
function effectiveWeight(item: RotationItem): number {
  return typeof item.weight === "number" && item.weight > 0
    ? item.weight
    : item.weight === 0
      ? 0
      : 1;
}

/**
 * Pick the item for play index `counter` (0-based) from `items`.
 * - sequential: walk the pool in given order (cursor = counter % n).
 * - even: cycle through ALL items before repeating; reshuffle each full cycle
 *   (seeded per cycle) so distribution is even but order varies per day/seed.
 * - weighted: deterministic cumulative-weight pick, independent per counter.
 * Returns null for an empty pool.
 */
export function pickRotationItem(
  items: readonly RotationItem[],
  mode: RotationMode,
  key: string,
  counter: number,
): RotationItem | null {
  const n = items.length;
  if (n === 0) return null;
  const idx = ((counter % n) + n) % n; // safe for negative counters

  if (mode === "sequential") {
    return items[idx];
  }

  if (mode === "even") {
    const cycle = Math.floor(counter / n);
    const order = seededShuffle(items, `${key}:cycle:${cycle}`);
    return order[idx];
  }

  // weighted: normalize weights, pick by cumulative threshold from a per-counter
  // deterministic float. Falls back to even-ish if all weights are zero.
  const weights = items.map(effectiveWeight);
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return items[idx];
  }
  const r = unitFloat(`${key}:w:${counter}`) * total;
  let acc = 0;
  for (let i = 0; i < n; i += 1) {
    acc += weights[i];
    if (r < acc) return items[i];
  }
  return items[n - 1];
}

/**
 * Draws per playable item when materializing a "weighted" rotation sequence.
 * Large enough that relative weights dominate within one cycled pass; the
 * array is derived independently (never shipped), so size only costs memory.
 */
export const WEIGHTED_ROTATION_DRAWS_PER_ITEM = 8;

/**
 * Materialize a playlist's rotation ORDER as a finite sequence that every
 * walker cycles identically — the server's sync-group segment timeline and
 * each shell's local rotation derive indices into this exact array, so all of
 * them MUST pass the same `key` (shuffleSeedForPlaylist of the group seed +
 * playlist id) or orders diverge.
 * - sequential: the pool in given (position) order.
 * - even: one seeded permutation of the pool (the legacy `shuffle` boolean
 *   maps here, so pre-rotationMode playlists keep their byte-identical order).
 * - weighted: WEIGHTED_ROTATION_DRAWS_PER_ITEM draws per item through
 *   pickRotationItem — the same weighted pick the arbitration composer uses —
 *   so heavier items repeat proportionally within the cycled sequence, and
 *   every item with effective weight > 0 is guaranteed at least one slot per
 *   cycle (the draws alone could starve a light item for a whole day — a 9:1
 *   pair misses the light item in one of 16 draws often enough to matter).
 */
export function materializeRotation<T extends RotationItem>(
  items: readonly T[],
  mode: RotationMode,
  key: string,
): T[] {
  if (items.length === 0) return [];
  if (mode === "sequential") return [...items];
  if (mode === "even") return seededShuffle(items, key);
  const length = items.length * WEIGHTED_ROTATION_DRAWS_PER_ITEM;
  const out: T[] = [];
  for (let counter = 0; counter < length; counter += 1) {
    const picked = pickRotationItem(items, "weighted", key, counter);
    if (picked) out.push(picked as T);
  }
  // Starvation guard: splice each weight>0 item the draws missed into ONE
  // seed-derived position, so the guarantee holds while the sequence stays
  // fully deterministic (every walker derives the identical array from `key`).
  const present = new Set(out.map((it) => it.id));
  for (const item of items) {
    if (present.has(item.id) || effectiveWeight(item) <= 0) continue;
    const at = Math.floor(
      unitFloat(`${key}:presence:${item.id}`) * (out.length + 1),
    );
    out.splice(at, 0, item);
    present.add(item.id);
  }
  return out;
}
